/**
 * Number / time / token formatting utilities.
 * Mirrors benchmark/report.ts helpers for consistency.
 */

import {
  iconAttach,
  iconClock,
  iconExec,
  iconFork,
  iconGlobe,
  iconRead,
  iconSearch,
  iconWrite,
  iconWrench,
} from "./icons.js";

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function formatCost(n: number): string {
  return `$${n.toFixed(4)}`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const secs = Math.round(ms / 1000);
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  return mins > 0 ? `${mins}m${String(remSecs).padStart(2, "0")}s` : `${secs}s`;
}

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function formatPercent(ratio: number): string {
  return `${(Math.abs(ratio) * 100).toFixed(1)}%`;
}

/** Short actor label from session key. "agent:main:benchmark-..." → "main" */
export function shortActor(actor: string): string {
  if (!actor) return "agent";
  // "lite:subagent:fd4feefa" → "lite:fd4feefa"
  const parts = actor.split(":");
  if (parts.length >= 3 && parts[1] === "subagent") {
    return `${parts[0]}:${parts[2].slice(0, 8)}`;
  }
  return parts[0] || "agent";
}

/** Tool icon SVG by name. Returns an inline SVG HTML string. */
export function toolIcon(toolName?: string): string {
  if (!toolName) return iconWrench();
  const name = toolName.toLowerCase();
  if (name === "read") return iconRead();
  if (name === "write" || name === "edit") return iconWrite();
  if (name === "exec") return iconExec();
  if (name === "web_search") return iconSearch();
  if (name === "web_fetch") return iconGlobe();
  if (name.includes("spawn") || name.includes("session")) return iconFork();
  if (name === "subagents") return iconClock();
  if (name === "attach") return iconAttach();
  return iconWrench();
}
